import { useEffect, useRef } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import { PlacementType, } from '@amzn/handshake-painter-typescript-client/v1';
import { HandshakePainterServiceApiV1 } from 'src/api/handshakeApi';
import { useAnalyticsContext } from 'src/common/metrics';
import { getMarketplace } from 'src/utils/marketplace';
import { partitionAsins } from 'src/utils/asinListUtils';
import { useDefaultApiParams } from './useDefaultApiParams';
/**
 * Maps a batch error to a failure category from its HTTP status, so a bad ad link is
 * distinguishable from a broken dependency in metrics. Only invoked inside an `isError` branch.
 *
 * A real network outage rejects `fetch` with a `TypeError` that never reaches the interceptor's
 * response-shaping (which only runs when an HTTP `response` exists), so it surfaces with no
 * `status` — hence the `status == null` guard, not `status === 0`.
 */
const categorizeError = (error) => {
    const status = error.status;
    if (status === 408 || status === 504)
        return 'timeout';
    if (status == null)
        return 'network-unavailable';
    if (status === 400 || status === 401 || status === 403 || status === 429)
        return 'request-rejected';
    return 'dependency-error';
};
/**
 * Batched product loader for the multi-ASIN landing page. Partitions the accepted ASIN list and
 * issues one GET_PRODUCT_WIDGET request per batch in parallel, exposing their combined state.
 * Invoked once from App.tsx behind `enabled`; the page reads the result from page context.
 */
export const useMultiAsinProducts = (options) => {
    var _a;
    const { asins } = options;
    const isEnabled = (_a = options.enabled) !== null && _a !== void 0 ? _a : true;
    const [searchParams] = useSearchParams();
    const { getDefaultParams } = useDefaultApiParams();
    const rumClient = useAnalyticsContext();
    const batches = partitionAsins(asins);
    const batchResults = useQueries({
        queries: batches.map((batch) => ({
            queryKey: ['getProductWidget', batch],
            queryFn: () => {
                const defaultApiParams = getDefaultParams();
                return HandshakePainterServiceApiV1.getProductWidget(Object.assign({ asins: batch, placement: PlacementType.CustomersAlsoBought }, defaultApiParams));
            },
            staleTime: Infinity,
            enabled: isEnabled,
        })),
    });
    // Resolved batches concatenated in batch order; failed/unresolved batches omitted.
    const products = batchResults.flatMap((result) => { var _a, _b; return (_b = (_a = result.data) === null || _a === void 0 ? void 0 : _a.products) !== null && _b !== void 0 ? _b : []; });
    // First-batch-only loading, matching useDealsStorefront.
    const isLoading = isEnabled && batches.length > 0 ? batchResults[0].isLoading : false;
    const asinsKey = asins.join(',');
    const marketplaceId = getMarketplace(searchParams);
    // Fire-once page-load event (page view, or no-valid-ASINs), keyed on the accepted ASIN list.
    const loggedPageLoadRef = useRef(null);
    useEffect(() => {
        if (!isEnabled || loggedPageLoadRef.current === asinsKey)
            return;
        loggedPageLoadRef.current = asinsKey;
        if (asins.length === 0) {
            rumClient.recordEvent('MultiAsinNoValidAsins', { url: window.location.href });
        }
        else {
            rumClient.recordEvent('MultiAsinPageView', {
                asinCount: asins.length,
                batchCount: batches.length,
                marketplaceId,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEnabled, asinsKey]);
    // Fire one MultiAsinBatchError per failed batch, once each, keyed on the ASIN list. Tracks
    // already-logged batch indices (not an aggregate flag) so a batch that fails later than the
    // first still emits its event.
    const loggedBatchErrorsRef = useRef({
        key: null,
        indices: new Set(),
    });
    useEffect(() => {
        if (!isEnabled)
            return;
        if (loggedBatchErrorsRef.current.key !== asinsKey) {
            loggedBatchErrorsRef.current = { key: asinsKey, indices: new Set() };
        }
        const logged = loggedBatchErrorsRef.current.indices;
        batchResults.forEach((result, index) => {
            if (result.isError && !logged.has(index)) {
                logged.add(index);
                rumClient.recordEvent('MultiAsinBatchError', {
                    batchIndex: index,
                    batchAsinCount: batches[index].length,
                    failureCategory: categorizeError(result.error),
                });
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEnabled, asinsKey, batchResults]);
    return { isLoading, products, batches, batchResults };
};
